import { config, GENDER, REPRO_KIND, PREGNANCY_OUTCOME } from './config';
import { normalizePregnancyRecord } from './pregnancyNormalize';

// Track B (pregnancy-record model): the pedigree DERIVES its pregnancy + gamete-donor
// render nodes from the carrier's pregnancy records (Pregnancy collection, surfaced on each
// member as `pregnancies: [record]`). Derived nodes are NOT persons — they exist only in
// the render's normalized node array, so they never get a UPN, a person sidebar, or a place
// in member-based exports. This is the seam that replaces the B1a/B1b config-schema storage;
// the layout engine + Track A symbols/lines consume the output unchanged.
//
// A pregnancy record where `conceivedWith === 'partner'` (or 'partnerAndDonor') descends
// from the carrier+partner union; a donor-conceived one hangs off the carrier alone and the
// donors are satellites (donorOfPids). A live birth (or a record with `bornMemberId`) renders
// NO pregnancy node — it has become a real child; the record is kept only as history. A
// `hidden` record is kept (still listed in the Reproductive sidebar) but not drawn.
//
// PURE + no d3 → unit-testable. Inert (returns the input array) when the flag is off or no
// member carries a pregnancy record, so pedigrees without reproductive data are unchanged.

const DONOR_GENDER = {
  sperm: GENDER.male,
  ovum: GENDER.female,
  embryo: GENDER.unknown
};

const rendersAsPregnancyNode = (rec) =>
  !!rec &&
  !rec.hidden &&
  rec.outcome !== PREGNANCY_OUTCOME.liveBirth &&
  rec.bornMemberId == null;

const partnerConceived = (rec) =>
  rec.conceivedWith === 'partner' || rec.conceivedWith === 'partnerAndDonor';

export function deriveReproNodes(members) {
  if (!config.ENABLE_REPRO_NODES || !Array.isArray(members)) {
    return members || [];
  }
  const anyRecords = members.some(
    (m) => Array.isArray(m.pregnancies) && m.pregnancies.length > 0
  );
  if (!anyRecords) return members; // byte-identical when there are no pregnancy records

  // Shallow-copy every member so childrenIds can be extended without mutating the input.
  const result = members.map((m) => ({
    ...m,
    childrenIds: [...(m.childrenIds || [])]
  }));
  const findByMemberId = (id) =>
    id == null
      ? null
      : result.find(
          (m) =>
            String(m._id) === String(id) ||
            String(m.uid) === String(id) ||
            String(m.pid) === String(id)
        );

  let nextPid =
    Math.max(0, ...result.map((m) => (typeof m.pid === 'number' ? m.pid : 0))) +
    1;
  const derived = [];

  for (const carrier of result) {
    const pregs = Array.isArray(carrier.pregnancies) ? carrier.pregnancies : [];
    for (const rawRec of pregs) {
      // Normalise FHQ-vs-Bennett outcome + field-name differences on read (legacy FHQ
      // records store `delivered`/`completedWeeks`/`twins_triplets`); pedigree-authored
      // records pass through unchanged.
      const rec = normalizePregnancyRecord(rawRec);
      if (!rendersAsPregnancyNode(rec)) {
        // A live birth / bornMemberId-linked record draws no pregnancy node (it became a
        // real child), but a donor-conceived birth keeps its gamete donor(s) visible — now
        // pointing at the born child, since the donor is genetically relevant to that person.
        const born =
          rec.bornMemberId != null ? findByMemberId(rec.bornMemberId) : null;
        const bornDonors =
          born && Array.isArray(rec.donors) ? rec.donors : [];
        const bornUid = String(rec._id ?? `preg-${carrier.pid}`);
        bornDonors.forEach((d, i) => {
          derived.push({
            pid: nextPid++,
            uid: `${bornUid}:${i}`,
            gender: DONOR_GENDER[d.role] ?? GENDER.unknown,
            reproKind: REPRO_KIND.gameteDonor,
            donorType: d.role,
            donorOfPids: [born.pid],
            spouseIds: [],
            childrenIds: [],
            siblingIds: []
          });
        });
        continue;
      }

      const pregPid = nextPid++;
      // `gender` is the assigned sex at birth: a non-male carrier occupies the mother slot
      // (they gestate). The other genetic parent slot is the in-graph partner only when the
      // pregnancy was partner-conceived; a donor is never a parent (it is a satellite).
      const carrierIsMother = carrier.gender !== GENDER.male;
      const partner = partnerConceived(rec)
        ? findByMemberId(rec.otherParentMemberId)
        : null;

      const uid = String(rec._id ?? `preg-${carrier.pid}-${pregPid}`);
      const pregNode = {
        pid: pregPid,
        uid,
        gender:
          rec.fetalSexKnown && rec.fetalSex ? rec.fetalSex : GENDER.unknown,
        isFetus: true,
        reproKind: REPRO_KIND.pregnancy,
        pregnancy: {
          outcome: rec.outcome,
          gestationalWeeks: rec.gestationalWeeks,
          fetalSexKnown: !!rec.fetalSexKnown,
          sexDeterminationMethod: rec.sexDeterminationMethod,
          karyotype: rec.karyotype,
          multiplicity: rec.multiplicity
        },
        motherId: carrierIsMother ? carrier.pid : partner ? partner.pid : null,
        fatherId: carrierIsMother
          ? partner
            ? partner.pid
            : null
          : carrier.pid,
        spouseIds: [],
        childrenIds: [],
        siblingIds: []
      };
      carrier.childrenIds.push(pregPid);
      if (partner) partner.childrenIds.push(pregPid);
      derived.push(pregNode);

      // Gamete donors — satellites carrying donorOfPids (never childrenIds / a mating line).
      const donors = Array.isArray(rec.donors) ? rec.donors : [];
      donors.forEach((d, i) => {
        derived.push({
          pid: nextPid++,
          uid: `${uid}:${i}`,
          gender: DONOR_GENDER[d.role] ?? GENDER.unknown,
          reproKind: REPRO_KIND.gameteDonor,
          donorType: d.role,
          donorOfPids: [pregPid],
          spouseIds: [],
          childrenIds: [],
          siblingIds: []
        });
      });
    }
  }

  return [...result, ...derived];
}
